import { useState, useEffect } from 'react';
import api, { formatError } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Pencil, Trash2 } from 'lucide-react';

const emptyBranch = { name: '', address: '', city: '', phone: '', email: '', manager_id: '', status: 'active' };

export default function BranchesPage() {
  const [branches, setBranches] = useState([]);
  const [managers, setManagers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyBranch);

  const fetchData = async () => {
    try {
      const [bRes, mRes] = await Promise.all([
        api.get('/branches'),
        api.get('/users', { params: { role: 'manager' } }).catch(() => ({ data: [] }))
      ]);
      setBranches(bRes.data);
      setManagers(mRes.data);
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  useEffect(() => { fetchData(); }, []);

  const openNew = () => { setEditing(null); setForm({...emptyBranch}); setDialogOpen(true); };
  const openEdit = (b) => { setEditing(b); setForm({ name: b.name, address: b.address || '', city: b.city || '', phone: b.phone || '', email: b.email || '', manager_id: b.manager_id || '', status: b.status }); setDialogOpen(true); };

  const handleSave = async () => {
    try {
      if (editing) { await api.put(`/branches/${editing.id}`, form); toast.success('Branch updated'); }
      else { await api.post('/branches', form); toast.success('Branch created'); }
      setDialogOpen(false); fetchData();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this branch?')) return;
    try { await api.delete(`/branches/${id}`); toast.success('Branch deleted'); fetchData(); }
    catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };

  const getManagerName = (id) => managers.find(m => m.id === id)?.name || '-';

  return (
    <div className="space-y-6" data-testid="branches-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-semibold text-brand-text-primary tracking-tight">Branches</h1>
          <p className="text-sm text-brand-text-secondary mt-1">Manage office locations and assignments</p>
        </div>
        <Button onClick={openNew} className="bg-brand-primary hover:bg-brand-primary-hover text-white rounded-sm" data-testid="add-branch-btn">
          <Plus size={16} className="mr-2" /> Add Branch
        </Button>
      </div>
      <Card className="border border-brand-border bg-white">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="branches-table">
              <thead>
                <tr className="border-b border-brand-border bg-brand-bg/50">
                  {['Name', 'Address', 'City', 'Phone', 'Email', 'Manager', 'Status', 'Actions'].map(h => (
                    <th key={h} className="text-left py-3 px-4 text-xs uppercase tracking-[0.15em] font-semibold text-brand-text-secondary">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading ? <tr><td colSpan={8} className="py-12 text-center text-brand-text-secondary">Loading...</td></tr> :
                 branches.length === 0 ? <tr><td colSpan={8} className="py-12 text-center text-brand-text-secondary">No branches found</td></tr> :
                 branches.map(b => (
                  <tr key={b.id} className="border-b border-brand-border/50 hover:bg-brand-surface-hover transition-colors">
                    <td className="py-3 px-4 font-medium text-brand-text-primary">{b.name}</td>
                    <td className="py-3 px-4 text-brand-text-secondary">{b.address || '-'}</td>
                    <td className="py-3 px-4 text-brand-text-secondary">{b.city || '-'}</td>
                    <td className="py-3 px-4 text-brand-text-secondary">{b.phone || '-'}</td>
                    <td className="py-3 px-4 text-brand-text-secondary">{b.email || '-'}</td>
                    <td className="py-3 px-4 text-brand-text-secondary">{getManagerName(b.manager_id)}</td>
                    <td className="py-3 px-4"><Badge className={`text-xs border-0 ${b.status === 'active' ? 'bg-brand-success/15 text-brand-success' : 'bg-gray-100 text-gray-600'}`}>{b.status}</Badge></td>
                    <td className="py-3 px-4">
                      <div className="flex gap-1">
                        <Button variant="ghost" size="icon" onClick={() => openEdit(b)} className="h-8 w-8" data-testid={`edit-branch-${b.id}`}><Pencil size={14} /></Button>
                        <Button variant="ghost" size="icon" onClick={() => handleDelete(b.id)} className="h-8 w-8 text-brand-error" data-testid={`delete-branch-${b.id}`}><Trash2 size={14} /></Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md" data-testid="branch-dialog">
          <DialogHeader><DialogTitle className="font-heading text-xl">{editing ? 'Edit Branch' : 'New Branch'}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2"><Label>Name *</Label><Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} data-testid="branch-name-input" className="bg-white border-brand-border rounded-sm" /></div>
            <div className="space-y-2"><Label>Address</Label><Input value={form.address} onChange={e => setForm({...form, address: e.target.value})} data-testid="branch-address-input" className="bg-white border-brand-border rounded-sm" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2"><Label>City</Label><Input value={form.city} onChange={e => setForm({...form, city: e.target.value})} data-testid="branch-city-input" className="bg-white border-brand-border rounded-sm" /></div>
              <div className="space-y-2"><Label>Phone</Label><Input value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} placeholder="+91..." data-testid="branch-phone-input" className="bg-white border-brand-border rounded-sm" /></div>
            </div>
            <div className="space-y-2"><Label>Email</Label><Input type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} data-testid="branch-email-input" className="bg-white border-brand-border rounded-sm" /></div>
            <div className="space-y-2">
              <Label>Manager</Label>
              <Select value={form.manager_id} onValueChange={v => setForm({...form, manager_id: v})}>
                <SelectTrigger className="bg-white border-brand-border rounded-sm" data-testid="branch-manager-select"><SelectValue placeholder="Select manager" /></SelectTrigger>
                <SelectContent>{managers.map(m => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Status</Label>
              <Select value={form.status} onValueChange={v => setForm({...form, status: v})}>
                <SelectTrigger className="bg-white border-brand-border rounded-sm"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="active">Active</SelectItem><SelectItem value="inactive">Inactive</SelectItem></SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="border-brand-border rounded-sm">Cancel</Button>
            <Button onClick={handleSave} className="bg-brand-primary hover:bg-brand-primary-hover text-white rounded-sm" data-testid="branch-save-btn">{editing ? 'Update' : 'Create'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
